import { defineStore } from 'pinia'
import findIndex from 'lodash/findIndex'
import { useMainStore } from '~/store/MainStore'
import { defaultError } from '~/classes/ErrorHandlers'
import type { BpProduct, BpShopCategory, StoreResponse } from '~/definitions/AllDefinitions'
import type { ProductStoreInterface } from '~/definitions/StateDefinitions'

export const useProductStore = defineStore('product-store',
  {
    state: (): ProductStoreInterface => ({
      shopCategories: [] as BpShopCategory[],
    }),
    getters: {
      categories: state => state.shopCategories,
    },
    actions: {
      baseUrl() {
        const store = useMainStore()
        if (!store.accountId) {
          return null
        }

        // If locationId is set, use it
        if (store.locationId) {
          return `/api/${store.accountId}/locations/${store.locationId}/categories`
        }

        if (store.currentLocations && store.currentLocations.length > 0) {
          const firstLocationId = store.currentLocations[0].id
          return `/api/${store.accountId}/locations/${firstLocationId}/categories`
        }

        return null
      },
      destroyProduct(shopCategoryId: number, productId: number) {
        const categoryIdx = findIndex(this.shopCategories, (category) => {
          return category.id === shopCategoryId
        })
        if (categoryIdx > -1) {
          const productIdx = findIndex(this.shopCategories[categoryIdx].products, (product) => {
            return product.id === productId
          })
          if (productIdx > -1) {
            this.shopCategories[categoryIdx].products!.splice(productIdx, 1)
            if (!this.shopCategories[categoryIdx].products!.length) {
              this.shopCategories.splice(categoryIdx, 1)
            }
          }
        }
      },
      destroyShopCategory(categoryId: number) {
        const idx = findIndex(this.shopCategories, (category) => {
          return category.id === categoryId
        })
        if (idx > -1) {
          this.shopCategories.splice(idx, 1)
        }
      },
      async getItem(shopCategoryId: number, productId: number) {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        const mainStore = useMainStore()
        const ret: StoreResponse = await mainStore.checkIsOrderingEnabled()
        if (ret.error) {
          return ret
        }
        try {
          const data = await apiFetch(`${url}/${shopCategoryId}/products/${productId}`)
          return {
            success: true,
            data: {
              item: data as BpProduct,
              isOrderingEnabled: ret.data,
            },
            error: null,
          } as StoreResponse
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          } as StoreResponse
        }
      },
      async getShopCategory(categoryId: number) {
        const url = this.baseUrl()
        if (!url) {
          return null
        }
        try {
          const data = await apiFetch(`${url}/${categoryId}`)
          return data ? data as BpShopCategory : null
        } catch {
          return null
        }
      },
      async getShopCategories() {
        const url = this.baseUrl()
        if (!url) {
          this.shopCategories = []
          return
        }
        try {
          const data = await apiFetch<BpShopCategory[]>(url, {
            headers: {
              'X-API-VERSION': '2',
            },
            query: {
              archived: false,
            },
          })
          if (data) {
            this.shopCategories = data
          }
        } catch {
          this.shopCategories = []
        }
      },
      async updateCreateProduct(shopCategoryId: number, productId: number) {
        const ret = await this.getItem(shopCategoryId, productId)
        if (ret.success && ret.data) {
          const categoryIdx = findIndex(this.shopCategories, (category) => {
            return category.id === shopCategoryId
          })
          if (categoryIdx > -1) {
            const productIdx = findIndex(this.shopCategories[categoryIdx].products, (product) => {
              return product.id === productId
            })
            if (typeof this.shopCategories[categoryIdx].products === 'undefined') {
              this.shopCategories[categoryIdx].products = [] as BpProduct[]
            }
            if (productIdx > -1 && this.shopCategories[categoryIdx] && this.shopCategories[categoryIdx].products &&
              this.shopCategories[categoryIdx].products!.length) {
              this.shopCategories[categoryIdx].products!.splice(productIdx, 1, ret.data.item as BpProduct)
            } else {
              this.shopCategories[categoryIdx].products!.push(ret.data.item as BpProduct)
            }
          } else {
            await this.updateCreateShopCategory(shopCategoryId)
          }
        }
      },
      async updateCreateShopCategory(categoryId: number) {
        const categoryUpdate = await this.getShopCategory(categoryId)
        if (categoryUpdate) {
          const idx = findIndex(this.shopCategories, (category) => {
            return category.id === categoryId
          })
          if (idx > -1) {
            if (categoryUpdate) {
              this.shopCategories.splice(idx, 1, categoryUpdate)
            }
          } else {
            this.shopCategories.push(categoryUpdate)
          }
        }
      },
    },
  })
